/**
 * Единый конверт ошибок бэкенда: `{ error, message, details? }`
 * (server/lib/http/error-middleware.ts). Ветвиться нужно по `error` — это стабильный
 * машинный код; `message` — уже локализованный текст для показа пользователю.
 *
 * ⚠️ Локализован он ровно до тех пор, пока ответ пришёл ОТ НАШЕГО API. Когда до бэкенда
 * дойти не удалось — оборвалась сеть, истёк таймаут, прокси или WAF ответил своей
 * HTML-страницей, — конверта нет, и `message` придумывает сам клиент. Такие коды
 * перечислены в `TRANSPORT_ERROR_CODES`, и их текст показывать НЕЛЬЗЯ: он английский и
 * технический. Ровно это владелец и увидел в модалке оплаты как
 * «Unexpected non-JSON response».
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fieldErrors: Record<string, string>;
  readonly details: unknown;

  constructor(
    status: number,
    code: string,
    message: string,
    details: unknown,
    fieldErrors: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.fieldErrors = fieldErrors;
  }
}

interface ValidationIssue {
  path: string[];
  code: string;
  message: string;
}

function isValidationDetails(details: unknown): details is ValidationIssue[] {
  return (
    Array.isArray(details) &&
    details.every(
      (issue) =>
        typeof issue === 'object' &&
        issue !== null &&
        Array.isArray((issue as { path?: unknown }).path) &&
        typeof (issue as { message?: unknown }).message === 'string',
    )
  );
}

/**
 * ⚠️ `details` НЕ всегда массив: в dev на 500 туда кладут `err.stack` строкой
 * (error-middleware.ts:163). Поэтому проверка структурная, а не по факту наличия поля.
 * В таком случае fieldErrors пустой, а исходный details долетает как есть — для консоли.
 *
 * `path` приходит МАССИВОМ сегментов (`['items','0','id']`), а не строкой с точками.
 * Склейка через '.' даёт путь, который понимают потребители форм для вложенных полей.
 */
export function normalizeErrorBody(status: number, body: unknown): ApiError {
  if (typeof body !== 'object' || body === null) {
    return new ApiError(status, 'unknown_error', `Unexpected error body (status ${status})`, body, {});
  }
  const envelope = body as { error?: unknown; message?: unknown; details?: unknown };
  const code = typeof envelope.error === 'string' ? envelope.error : 'unknown_error';
  const message =
    typeof envelope.message === 'string'
      ? envelope.message
      : `Response envelope without message (status ${status})`;

  const fieldErrors: Record<string, string> = {};
  if (isValidationDetails(envelope.details)) {
    for (const issue of envelope.details) {
      // Один и тот же путь у нескольких issue: побеждает последний — в zod более
      // специфичная проверка обычно идёт после базовой.
      fieldErrors[issue.path.map(String).join('.')] = issue.message;
    }
  }
  return new ApiError(status, code, message, envelope.details, fieldErrors);
}

/**
 * Коды, которые клиент ставит САМ, не получив разборчивого ответа от API.
 *
 * ⚠️ Признак у них общий и важный: `message` такой ошибки НЕ ПРИХОДИЛ с сервера, а значит
 * не локализован и предназначен для консоли и разбора, а не для человека. Показывать его
 * в интерфейсе — нарушение §0 CLAUDE.md, которое гейт `npm run i18n:lint` не ловит по
 * построению: он ищет армянский, кириллицу и гомоглифы, английский литерал проходит молча.
 *
 * `unknown_error` в этом списке потому, что сервер такого кода не отдаёт ни на одном
 * маршруте (проверено grep'ом по `server/`) — его ставит `normalizeErrorBody`, когда тело
 * ответа не является конвертом.
 */
export const TRANSPORT_ERROR_CODES: readonly string[] = [
  'non_json_response',
  'timeout',
  'network_error',
  'unknown_error',
  /**
   * ⚠️ Единственный код в списке, который приходит НЕ от клиента, а от nginx: его отдаёт
   * `location @api_edge_error` в `../MathMindInfrastructure`, когда контейнер приложения
   * не отвечает (выкатка, перезапуск, падение). Конверт там намеренно без текста — писать
   * сообщение в конфиге nginx значило бы завести видимую строку вне каталога локали.
   *
   * Имя РАЗЛИЧАЕТСЯ с `gateway_unavailable` приложения намеренно: тот означает «не отвечает
   * платёжный шлюз» и несёт свой армянский текст. Один код на два события не дал бы
   * ответить, банк это лёг или наш контейнер.
   */
  'edge_unavailable',
];

/**
 * Ошибка, у которой нет человеческого текста от сервера. Потребитель обязан показать
 * СВОЙ текст из каталога, а `message` оставить логам.
 */
export function isTransportError(error: unknown): boolean {
  return error instanceof ApiError && TRANSPORT_ERROR_CODES.includes(error.code);
}
